import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { eur, pct, MODE_LABEL } from "@/lib/format";
import { Modal, Field, inputCls, useAsync } from "./ui";

const MODES = Object.keys(MODE_LABEL);

export default function SituationModal({ quoteId, state, onClose, onCreated }) {
  const [mode, setMode] = useState("global_pct");
  const [form, setForm] = useState({ pct: "", amount_ht: "", motif: "", situation_index: "", periode_du: "", periode_au: "", observation: "", date_echeance: "", retenue_pct: 0 });
  const [sections, setSections] = useState({});
  const [lines, setLines] = useState({});
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const { busy, run } = useAsync();

  const sectionsList = useMemo(() => {
    const m = new Map();
    state.lines.forEach(l => { if (!m.has(l.section_id)) m.set(l.section_id, { id: l.section_id, titre: l.section_titre, prior: l.prior_pct, lines: [] }); m.get(l.section_id).lines.push(l); });
    return [...m.values()];
  }, [state]);

  const body = () => {
    const b = { mode, motif: form.motif, observation: form.observation, periode_du: form.periode_du, periode_au: form.periode_au, date_echeance: form.date_echeance,
      retenue_pct: Number(form.retenue_pct || 0), situation_index: form.situation_index ? Number(form.situation_index) : null };
    if (mode === "global_pct") b.pct = Number(form.pct || 0);
    if (mode === "free_amount") b.amount_ht = Number(form.amount_ht || 0);
    if (mode === "per_section") b.sections = Object.fromEntries(Object.entries(sections).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)]));
    if (mode === "per_line") b.lines = Object.fromEntries(Object.entries(lines).filter(([, v]) => v.cumul_pct !== undefined && v.cumul_pct !== "").map(([k, v]) => [k, { cumul_pct: Number(v.cumul_pct) }]));
    if (mode === "quantities") b.lines = Object.fromEntries(Object.entries(lines).filter(([, v]) => v.qty_cumul !== undefined && v.qty_cumul !== "").map(([k, v]) => [k, { qty_cumul: Number(v.qty_cumul) }]));
    if (mode === "manual") b.lines = Object.fromEntries(Object.entries(lines).filter(([, v]) => v.selected).map(([k, v]) => [k, { pct_periode: Number(v.pct_periode ?? 0) }]));
    return b;
  };

  useEffect(() => {
    const t = setTimeout(async () => {
      try { const { data } = await api.post(`/billing/quotes/${quoteId}/situations/preview`, body()); setPreview(data); setErr(""); }
      catch (e) { setPreview(null); setErr(e?.response?.data?.detail || ""); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, form, sections, lines]);

  const create = () => run(async () => {
    try {
      const { data } = await api.post(`/billing/quotes/${quoteId}/situations`, body());
      toast.success(`Situation n°${data.situation_index} enregistrée en brouillon`); onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  });

  const setLine = (id, k, v) => setLines(p => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));

  return (
    <Modal title="Nouvelle facture de situation" subtitle={`Marché ${eur(state.marche_ttc)} TTC · déjà facturé ${pct(state.pct_facture)}`} onClose={onClose} wide testId="situation-modal">
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-5">
          <div>
            <div className="text-xs font-mono-caption text-ink-soft mb-2">Méthode de calcul</div>
            <div className="flex flex-wrap gap-2">
              {MODES.map(m => (
                <button key={m} type="button" data-testid={`sit-mode-${m}`} onClick={() => setMode(m)}
                  className={`px-3 py-2 rounded-full border text-xs font-medium transition-colors ${mode === m ? "bg-anthracite text-ivory border-anthracite" : "bg-white border-black/15 text-anthracite hover:border-anthracite"}`}>
                  {MODE_LABEL[m]}
                </button>
              ))}
            </div>
          </div>

          {mode === "global_pct" && (
            <Field label="Pourcentage à facturer sur cette situation" hint={`Reste facturable : ${pct(100 - state.pct_facture)} du marché`}>
              <input type="number" min="0" max="100" step="0.5" data-testid="sit-pct" className={inputCls} value={form.pct} onChange={e => setForm({ ...form, pct: e.target.value })} placeholder="Ex : 20" />
            </Field>
          )}
          {mode === "free_amount" && (
            <Field label="Montant HT de la situation" hint={`Reste à facturer : ${eur(state.reste_a_facturer_ht)} HT — le montant est réparti proportionnellement sur les prestations restantes.`}>
              <input type="number" min="0" step="0.01" data-testid="sit-amount" className={inputCls} value={form.amount_ht} onChange={e => setForm({ ...form, amount_ht: e.target.value })} />
            </Field>
          )}
          {mode === "per_section" && (
            <div className="space-y-2">
              <div className="text-xs font-mono-caption text-ink-soft">Avancement cumulé par lot (en %)</div>
              {sectionsList.map(s => (
                <div key={s.id} className="flex items-center gap-3 bg-white border border-black/10 rounded-rama-sm p-3">
                  <div className="flex-1"><div className="text-sm font-medium">{s.titre}</div><div className="text-xs text-ink-soft">Déjà facturé : {pct(s.prior)}</div></div>
                  <input type="number" min="0" max="100" data-testid={`sit-section-${s.id}`} className={`${inputCls} w-28`} placeholder={String(Math.round(s.prior))} value={sections[s.id] ?? ""} onChange={e => setSections({ ...sections, [s.id]: e.target.value })} />
                </div>
              ))}
            </div>
          )}
          {(mode === "per_line" || mode === "quantities" || mode === "manual") && (
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              <div className="text-xs font-mono-caption text-ink-soft">
                {mode === "per_line" ? "Avancement cumulé par prestation (en %)" : mode === "quantities" ? "Quantité exécutée cumulée par prestation" : "Sélectionnez les prestations et le % de la période"}
              </div>
              {state.lines.map(l => (
                <div key={l.line_id} className="flex items-center gap-3 bg-white border border-black/10 rounded-rama-sm p-3">
                  {mode === "manual" && <input type="checkbox" data-testid={`sit-select-${l.line_id}`} checked={!!lines[l.line_id]?.selected} onChange={e => setLine(l.line_id, "selected", e.target.checked)} />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{l.nom}</div>
                    <div className="text-xs text-ink-soft">{l.section_titre} · {l.quantite} {l.unite} · {eur(l.montant_ht)} HT · facturé {pct(l.prior_pct)}{mode === "quantities" ? ` (${l.prior_qty} ${l.unite})` : ""}</div>
                  </div>
                  {mode === "per_line" && <input type="number" min="0" max="100" data-testid={`sit-line-${l.line_id}`} className={`${inputCls} w-24`} placeholder={String(Math.round(l.prior_pct))} value={lines[l.line_id]?.cumul_pct ?? ""} onChange={e => setLine(l.line_id, "cumul_pct", e.target.value)} />}
                  {mode === "quantities" && <input type="number" min="0" step="0.01" data-testid={`sit-qty-${l.line_id}`} className={`${inputCls} w-28`} placeholder={String(l.prior_qty)} value={lines[l.line_id]?.qty_cumul ?? ""} onChange={e => setLine(l.line_id, "qty_cumul", e.target.value)} />}
                  {mode === "manual" && <input type="number" min="0" max="100" data-testid={`sit-manual-${l.line_id}`} className={`${inputCls} w-24`} placeholder="% période" disabled={!lines[l.line_id]?.selected} value={lines[l.line_id]?.pct_periode ?? ""} onChange={e => setLine(l.line_id, "pct_periode", e.target.value)} />}
                </div>
              ))}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="N° de situation affiché"><input type="number" min="1" data-testid="sit-index" className={inputCls} value={form.situation_index} onChange={e => setForm({ ...form, situation_index: e.target.value })} placeholder="auto" /></Field>
            <Field label="Échéance de paiement"><input type="date" className={inputCls} value={form.date_echeance} onChange={e => setForm({ ...form, date_echeance: e.target.value })} /></Field>
            <Field label="Période du"><input type="date" className={inputCls} value={form.periode_du} onChange={e => setForm({ ...form, periode_du: e.target.value })} /></Field>
            <Field label="au"><input type="date" className={inputCls} value={form.periode_au} onChange={e => setForm({ ...form, periode_au: e.target.value })} /></Field>
          </div>
          <Field label="Observation (visible sur la facture)"><textarea rows={2} data-testid="sit-observation" className={inputCls} value={form.observation} onChange={e => setForm({ ...form, observation: e.target.value })} /></Field>
          <Field label={mode === "free_amount" ? "Motif (obligatoire)" : "Motif interne"}><input data-testid="sit-motif" className={inputCls} value={form.motif} onChange={e => setForm({ ...form, motif: e.target.value })} /></Field>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white border border-black/10 rounded-rama-md p-5 sticky top-4" data-testid="sit-preview">
            <div className="text-xs font-mono-caption text-ink-soft mb-3">Aperçu calculé par le serveur</div>
            {err && <div className="text-sm text-bordeaux-700 bg-bordeaux-light p-3 rounded-rama-sm" data-testid="sit-preview-error">{err}</div>}
            {!err && !preview && <div className="text-sm text-ink-soft">Renseignez les éléments pour voir l'impact.</div>}
            {preview && (
              <>
                <div className="font-display text-3xl" data-testid="sit-preview-ttc">{eur(preview.totals.total_ttc)} <span className="text-base text-bordeaux-700">TTC</span></div>
                <div className="text-xs text-ink-soft">{eur(preview.totals.total_ht, "always")} HT · TVA {eur(preview.totals.total_tva, "always")}</div>
                <div className="mt-4 space-y-1.5 text-sm">
                  {[["Marché actualisé", preview.summary.marche_ttc], ["Déjà facturé", preview.summary.anterieur_ttc], ["Cette situation", preview.summary.presente_ttc], ["Cumul après", preview.summary.cumul_ttc], ["Reste à facturer", preview.summary.reste_ttc]].map(([l, v]) => (
                    <div key={l} className="flex justify-between border-b border-black/5 py-1"><span className="text-ink-soft">{l}</span><span className="font-medium">{eur(v)}</span></div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-ink-soft">Lignes facturées : {preview.lines.filter(l => Math.abs(l.montant_ht) > 0.004).length} / {preview.lines.length}</div>
              </>
            )}
            <button data-testid="sit-create" disabled={!preview || busy} onClick={create} className="btn-primary w-full justify-center mt-5 disabled:opacity-50">Enregistrer en brouillon</button>
            <p className="text-xs text-ink-soft mt-2">Le brouillon pourra être prévisualisé en PDF puis émis (numérotation définitive).</p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
